import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  Users, Globe, CreditCard, Plus, CheckCircle, XCircle, AlertCircle,
  Building2, ChevronRight, Crown, Coins, ArrowRightLeft, UserMinus, Loader2,
} from 'lucide-react'
import { AssociationDetailSheet } from './AssociationDetailSheet'

// ─── constants ───────────────────────────────────────────────────────────────

const ECOWAS_COUNTRIES = [
  { code: 'BJ', name: 'Benin' }, { code: 'BF', name: 'Burkina Faso' },
  { code: 'CV', name: 'Cabo Verde' }, { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'GM', name: 'Gambia' }, { code: 'GH', name: 'Ghana' },
  { code: 'GN', name: 'Guinea' }, { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'LR', name: 'Liberia' }, { code: 'ML', name: 'Mali' },
  { code: 'MR', name: 'Mauritania' }, { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' }, { code: 'SN', name: 'Senegal' },
  { code: 'SL', name: 'Sierra Leone' }, { code: 'TG', name: 'Togo' },
]

const COMMON_CURRENCIES = [
  { code: 'USD', name: 'US Dollar' }, { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' }, { code: 'XOF', name: 'West African CFA Franc' },
  { code: 'XAF', name: 'Central African CFA Franc' }, { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'GHS', name: 'Ghanaian Cedi' }, { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'ZAR', name: 'South African Rand' }, { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'ETB', name: 'Ethiopian Birr' }, { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UGX', name: 'Ugandan Shilling' }, { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'GMD', name: 'Gambian Dalasi' }, { code: 'SLE', name: 'Sierra Leonean Leone' },
  { code: 'LRD', name: 'Liberian Dollar' }, { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'MWK', name: 'Malawian Kwacha' }, { code: 'ZMW', name: 'Zambian Kwacha' },
  { code: 'BWP', name: 'Botswana Pula' }, { code: 'NAD', name: 'Namibian Dollar' },
]

const STATUS_STYLE: Record<string, string> = {
  active:    'bg-[#E1F5EE] text-[#0F6E56]',
  grace:     'bg-[#FEF9E7] text-[#856404]',
  suspended: 'bg-[#FEE8E8] text-[#A32D2D]',
  pending:   'bg-[#EBF4FF] text-[#1E4E8C]',
  rejected:  'bg-muted text-muted-foreground',
}

const DUES_STYLE: Record<string, string> = {
  paid:     'bg-[#E1F5EE] text-[#0F6E56]',
  unpaid:   'bg-[#FEE8E8] text-[#A32D2D]',
  exempted: 'bg-[#F0E4B8] text-[#7A5C00]',
}

// ─── component ───────────────────────────────────────────────────────────────

export function AssociationsView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [selected, setSelected] = useState<any | null>(null)
  const [detailAssocId, setDetailAssocId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    name: '', slug: '', description: '', headquarter_country: '',
    region: '', contact_email: '', dues_amount: '', dues_currency: 'USD',
    dues_period: 'annual', grace_period_days: '30',
  })
  const [duesModal, setDuesModal] = useState<any | null>(null)
  const [duesForm, setDuesForm] = useState({
    period_start: '', period_end: '', amount: '', payment_method: 'manual',
    payment_reference: '', notes: '',
  })
  const [chapterModal, setChapterModal] = useState(false)
  const [chapterForm, setChapterForm] = useState({ country_code: '', country_name: '' })

  // leadership state
  const [assignModal, setAssignModal]     = useState<{ positionId: string; title: string } | null>(null)
  const [assignMemberId, setAssignMemberId] = useState('')
  const [transferModal, setTransferModal]  = useState(false)
  const [transferMemberId, setTransferMemberId] = useState('')
  const [vacateConfirm, setVacateConfirm]  = useState<string | null>(null) // leadership row id

  // currency state
  const [currencyModal, setCurrencyModal]  = useState(false)
  const [currencyForm, setCurrencyForm]    = useState({ currency_code: '', currency_name: '', exchange_rate_to_primary: '1' })

  // ── queries ──────────────────────────────────────────────────────────────

  const { data: associations } = useQuery({
    queryKey: ['associations'],
    queryFn: async () => {
      const { data } = await (supabase.from('associations' as any).select('*').order('name') as any)
      return (data || []) as any[]
    },
  })

  // Fetch current president for each association (batch query)
  const { data: presidentMap = {} } = useQuery({
    queryKey: ['associations-presidents', (associations || []).map((a: any) => a.id).join(',')],
    enabled: (associations || []).length > 0,
    queryFn: async () => {
      const ids = (associations || []).map((a: any) => a.id)
      const { data } = await (supabase
        .from('association_leadership' as any)
        .select(`
          association_id,
          association_positions!inner(title),
          association_members!inner(journalist_profiles(full_name))
        `)
        .in('association_id', ids)
        .eq('is_current', true)
        .eq('association_positions.title', 'President') as any)
      const map: Record<string, string> = {}
      ;(data || []).forEach((r: any) => {
        map[r.association_id] = r.association_members?.journalist_profiles?.full_name ?? ''
      })
      return map
    },
  })

  const { data: members, refetch: refetchMembers } = useQuery({
    queryKey: ['association-members', selected?.id],
    enabled: !!selected?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_members' as any)
        .select('*, journalist_profiles(full_name, email), association_chapters(country_name)')
        .eq('association_id', selected.id)
        .order('applied_at', { ascending: false }) as any)
      return (data || []) as any[]
    },
  })

  const { data: chapters } = useQuery({
    queryKey: ['association-chapters', selected?.id],
    enabled: !!selected?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_chapters' as any)
        .select('*').eq('association_id', selected.id).order('country_name') as any)
      return (data || []) as any[]
    },
  })

  const { data: linkedOrgs } = useQuery({
    queryKey: ['association-org-links', selected?.id],
    enabled: !!selected?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_org_links' as any)
        .select('*, organizations(name)').eq('association_id', selected.id) as any)
      return (data || []) as any[]
    },
  })

  const { data: positions } = useQuery({
    queryKey: ['association-positions', selected?.id],
    enabled: !!selected?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_positions' as any)
        .select('*').eq('association_id', selected.id)
        .eq('is_active', true).order('rank') as any)
      return (data || []) as any[]
    },
  })

  const { data: leadership, refetch: refetchLeadership } = useQuery({
    queryKey: ['association-leadership', selected?.id],
    enabled: !!selected?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_leadership' as any)
        .select(`
          *,
          association_positions(title, rank),
          association_members!inner(journalist_profiles(full_name, email))
        `)
        .eq('association_id', selected.id)
        .eq('is_current', true) as any)
      return (data || []) as any[]
    },
  })

  const { data: currencies, refetch: refetchCurrencies } = useQuery({
    queryKey: ['association-currencies', selected?.id],
    enabled: !!selected?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('association_currencies' as any)
        .select('*').eq('association_id', selected.id).order('is_primary', { ascending: false }) as any)
      return (data || []) as any[]
    },
  })

  const { data: memberCountsData } = useQuery({
    queryKey: ['association-member-counts'],
    queryFn: async () => {
      const { data } = await (supabase.from('association_members' as any).select('association_id, status') as any)
      const counts: Record<string, { total: number; active: number }> = {}
      for (const m of (data ?? [])) {
        if (!counts[m.association_id]) counts[m.association_id] = { total: 0, active: 0 }
        counts[m.association_id].total++
        if (m.status === 'active') counts[m.association_id].active++
      }
      return counts
    },
  })

  const memberCounts = (id: string) => memberCountsData?.[id] ?? { total: 0, active: 0 }

  // Active members for assignment dropdowns
  const activeMembers = (members || []).filter((m: any) => m.status === 'active')

  // Build lookup: positionId → leadership row (if filled)
  const filledMap = Object.fromEntries(
    (leadership || []).map((l: any) => [l.position_id, l])
  )

  // ── mutations ─────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.from('associations' as any).insert({
        name: createForm.name,
        slug: createForm.slug.toLowerCase().replace(/\s+/g, '-'),
        description: createForm.description || null,
        headquarter_country: createForm.headquarter_country || null,
        region: createForm.region || null,
        contact_email: createForm.contact_email || null,
        dues_amount: parseFloat(createForm.dues_amount) || 0,
        dues_currency: createForm.dues_currency,
        dues_period: createForm.dues_period,
        grace_period_days: parseInt(createForm.grace_period_days) || 30,
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Association created.' })
      qc.invalidateQueries({ queryKey: ['associations'] })
      setShowCreate(false)
      setCreateForm({ name: '', slug: '', description: '', headquarter_country: '', region: '', contact_email: '', dues_amount: '', dues_currency: 'USD', dues_period: 'annual', grace_period_days: '30' })
    },
    onError: () => toast({ title: 'Failed to create.', variant: 'destructive' }),
  })

  const approveMutation = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await (supabase.from('association_members' as any).update({
        status: approve ? 'active' : 'rejected',
        approved_at: approve ? new Date().toISOString() : null,
        approved_by: approve ? user?.id : null,
        updated_at: new Date().toISOString(),
      }).eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Member status updated.' }); refetchMembers() },
  })

  const markDuesMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      await (supabase.from('association_dues_payments' as any).insert({
        association_id: selected.id,
        journalist_id: duesModal.journalist_id,
        member_id: duesModal.id,
        amount: parseFloat(duesForm.amount),
        currency: selected.dues_currency,
        period_start: duesForm.period_start,
        period_end: duesForm.period_end,
        payment_method: duesForm.payment_method,
        payment_reference: duesForm.payment_reference || null,
        notes: duesForm.notes || null,
        marked_by: user?.id,
      }) as any)
      await (supabase.from('association_members' as any).update({
        dues_status: 'paid',
        dues_paid_until: duesForm.period_end,
        status: 'active',
        updated_at: new Date().toISOString(),
      }).eq('id', duesModal.id) as any)
    },
    onSuccess: () => {
      toast({ title: 'Dues marked as paid. Member set to Active.' })
      setDuesModal(null)
      setDuesForm({ period_start: '', period_end: '', amount: '', payment_method: 'manual', payment_reference: '', notes: '' })
      refetchMembers()
    },
    onError: () => toast({ title: 'Failed to record payment.', variant: 'destructive' }),
  })

  const suspendMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await (supabase.from('association_members' as any).update({
        status: 'suspended', dues_status: 'unpaid',
        suspended_at: new Date().toISOString(),
        suspension_reason: 'Manually suspended by administrator',
        updated_at: new Date().toISOString(),
      }).eq('id', memberId) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Member suspended.' }); refetchMembers() },
  })

  const addChapterMutation = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.from('association_chapters' as any).insert({
        association_id: selected.id,
        country_code: chapterForm.country_code,
        country_name: chapterForm.country_name,
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Chapter added.' })
      qc.invalidateQueries({ queryKey: ['association-chapters', selected.id] })
      setChapterModal(false)
      setChapterForm({ country_code: '', country_name: '' })
    },
    onError: () => toast({ title: 'Failed to add chapter.', variant: 'destructive' }),
  })

  // ── leadership mutations ──────────────────────────────────────────────────

  const assignPositionMutation = useMutation({
    mutationFn: async ({ positionId, memberId }: { positionId: string; memberId: string }) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')
      const member = activeMembers.find((m: any) => m.id === memberId) as any
      if (!member) throw new Error('Member not found')
      const { error } = await (supabase.from('association_leadership' as any).insert({
        association_id: selected.id,
        position_id:    positionId,
        member_id:      memberId,
        user_id:        member.journalist_id,
        assigned_by:    user.id,
        assignment_type: 'superadmin_assign',
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Position assigned.' })
      setAssignModal(null); setAssignMemberId('')
      refetchLeadership()
    },
    onError: (e: any) => toast({ title: 'Failed to assign.', description: e.message, variant: 'destructive' }),
  })

  const vacatePositionMutation = useMutation({
    mutationFn: async (leadershipId: string) => {
      const { error } = await (supabase.from('association_leadership' as any)
        .update({ tenure_end: new Date().toISOString().slice(0, 10), is_current: false, vacated_reason: 'removed' })
        .eq('id', leadershipId) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Position vacated.' }); setVacateConfirm(null); refetchLeadership() },
    onError: () => toast({ title: 'Failed to vacate.', variant: 'destructive' }),
  })

  const transferPresidencyMutation = useMutation({
    mutationFn: async (newMemberId: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')
      const { error } = await (supabase.rpc('transfer_association_presidency' as any, {
        p_association_id:  selected.id,
        p_new_member_id:   newMemberId,
        p_assigned_by:     user.id,
        p_assignment_type: 'superadmin_assign',
      }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Presidency transferred. All other positions vacated.' })
      setTransferModal(false); setTransferMemberId('')
      refetchLeadership()
    },
    onError: (e: any) => toast({ title: 'Transfer failed.', description: e.message, variant: 'destructive' }),
  })

  // ── currency mutations ────────────────────────────────────────────────────

  const addCurrencyMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await (supabase.from('association_currencies' as any).upsert({
        association_id:           selected.id,
        currency_code:            currencyForm.currency_code.toUpperCase(),
        currency_name:            currencyForm.currency_name,
        exchange_rate_to_primary: parseFloat(currencyForm.exchange_rate_to_primary) || 1,
        is_primary:               false,
        is_active:                true,
        rate_updated_at:          new Date().toISOString(),
        updated_by:               user?.id,
      }, { onConflict: 'association_id,currency_code' }) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Currency added.' })
      setCurrencyModal(false)
      setCurrencyForm({ currency_code: '', currency_name: '', exchange_rate_to_primary: '1' })
      refetchCurrencies()
    },
    onError: (e: any) => toast({ title: 'Failed to add currency.', description: e.message, variant: 'destructive' }),
  })

  const toggleCurrencyMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await (supabase.from('association_currencies' as any).update({ is_active: active }).eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: () => refetchCurrencies(),
  })

  const deleteCurrencyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from('association_currencies' as any).delete().eq('id', id) as any)
      if (error) throw error
    },
    onSuccess: () => { toast({ title: 'Currency removed.' }); refetchCurrencies() },
    onError: () => toast({ title: 'Cannot remove primary currency.', variant: 'destructive' }),
  })

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[17px] font-semibold text-[#0D2240] dark:text-white">Associations</h2>
          <p className="text-justify text-[12px] text-muted-foreground mt-0.5">
            Manage journalist associations, membership dues, and institutional verification links.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] transition-colors">
          <Plus size={14} /> New Association
        </button>
      </div>

      {/* Association cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(associations || []).length === 0 && (
          <div className="col-span-2 text-center py-12 text-muted-foreground">
            <Globe size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-justify text-[13px]">No associations yet. Create your first one above.</p>
          </div>
        )}
        {(associations || []).map((assoc: any) => (
          <button key={assoc.id} onClick={() => setSelected(assoc)}
            className="text-left bg-background border border-border rounded-xl p-5 hover:border-[#006B3C]/40 transition-colors group">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[14px] font-semibold">{assoc.name}</div>
                {assoc.region && <div className="text-[11px] text-muted-foreground mt-0.5">{assoc.region}</div>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${assoc.status === 'active' ? 'bg-[#E1F5EE] text-[#0F6E56]' : 'bg-muted text-muted-foreground'}`}>
                  {assoc.status}
                </span>
                <button
                  onClick={e => { e.stopPropagation(); setDetailAssocId(assoc.id) }}
                  className="text-[10px] px-2 py-0.5 rounded border border-[#006B3C]/30 text-[#006B3C] hover:bg-[#E1F5EE] font-medium transition-colors"
                >
                  View Details
                </button>
                <ChevronRight size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </div>
            <div className="flex items-center gap-4 text-[11.5px] text-muted-foreground">
              <span className="flex items-center gap-1"><CreditCard size={11} />{assoc.dues_currency} {parseFloat(assoc.dues_amount || 0).toFixed(2)} / {assoc.dues_period}</span>
              <span className="flex items-center gap-1"><Globe size={11} />{assoc.grace_period_days}d grace</span>
              <span className="flex items-center gap-1"><Users size={11} />{memberCounts(assoc.id).total} · {memberCounts(assoc.id).active} active</span>
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
              {presidentMap[assoc.id] ? (
                <span className="flex items-center gap-1">
                  <Crown size={11} className="text-[#C8A84B]" />
                  President: <span className="font-medium text-foreground">{presidentMap[assoc.id]}</span>
                </span>
              ) : (
                <span className="flex items-center gap-1 text-amber-600">
                  <Crown size={11} />No president assigned
                </span>
              )}
              {assoc.contact_email && <span>{assoc.contact_email}</span>}
            </div>
          </button>
        ))}
      </div>

      {/* ─── Association detail sheet ─── */}
      <Sheet open={!!selected} onOpenChange={o => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full max-w-3xl overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-[16px]">{selected?.name}</SheetTitle>
            {selected?.region && <p className="text-justify text-[12px] text-muted-foreground">{selected.region}</p>}
          </SheetHeader>

          <Tabs defaultValue="members">
            <TabsList className="mb-4">
              <TabsTrigger value="members">Members</TabsTrigger>
              <TabsTrigger value="leadership">
                <Crown size={13} className="mr-1" /> Leadership
              </TabsTrigger>
              <TabsTrigger value="chapters">Chapters</TabsTrigger>
              <TabsTrigger value="orgs">Linked Institutions</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            {/* ── MEMBERS ── */}
            <TabsContent value="members" className="space-y-3">
              <p className="text-justify text-[12px] text-muted-foreground">
                {(members || []).length} total · {(members || []).filter((m: any) => m.status === 'active').length} active
              </p>
              {(members || []).length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-[13px]">No members yet.</div>
              )}
              {(members || []).map((m: any) => (
                <div key={m.id} className="bg-background border border-border rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-medium">{m.journalist_profiles?.full_name ?? 'Unknown'}</span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[m.status] ?? 'bg-muted text-muted-foreground'}`}>{m.status}</span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${DUES_STYLE[m.dues_status] ?? 'bg-muted text-muted-foreground'}`}>dues: {m.dues_status}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {m.journalist_profiles?.email}
                        {m.association_chapters?.country_name ? ` · ${m.association_chapters.country_name}` : ''}
                        {m.news_org ? ` · ${m.news_org}` : ''}
                      </div>
                      {m.dues_paid_until && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Paid until: {new Date(m.dues_paid_until).toLocaleDateString('en', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      {m.status === 'pending' && (
                        <>
                          <button onClick={() => approveMutation.mutate({ id: m.id, approve: true })}
                            className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-[#E1F5EE] text-[#0F6E56] font-medium hover:bg-[#006B3C] hover:text-white transition-colors">
                            <CheckCircle size={11} /> Approve
                          </button>
                          <button onClick={() => approveMutation.mutate({ id: m.id, approve: false })}
                            className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-[#FEE8E8] text-[#A32D2D] font-medium hover:bg-red-600 hover:text-white transition-colors">
                            <XCircle size={11} /> Reject
                          </button>
                        </>
                      )}
                      {(m.status === 'active' || m.status === 'grace') && (
                        <>
                          <button onClick={() => {
                            const today = new Date().toISOString().slice(0, 10)
                            const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10)
                            setDuesForm({ period_start: today, period_end: nextYear, amount: selected?.dues_amount?.toString() ?? '', payment_method: 'manual', payment_reference: '', notes: '' })
                            setDuesModal(m)
                          }}
                            className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-[#E1F5EE] text-[#0F6E56] font-medium hover:bg-[#006B3C] hover:text-white transition-colors">
                            <CreditCard size={11} /> Mark Paid
                          </button>
                          <button onClick={() => suspendMutation.mutate(m.id)}
                            className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-muted text-muted-foreground font-medium hover:bg-[#FEE8E8] hover:text-[#A32D2D] transition-colors">
                            <AlertCircle size={11} /> Suspend
                          </button>
                        </>
                      )}
                      {m.status === 'suspended' && (
                        <button onClick={() => {
                          const today = new Date().toISOString().slice(0, 10)
                          const nextYear = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10)
                          setDuesForm({ period_start: today, period_end: nextYear, amount: selected?.dues_amount?.toString() ?? '', payment_method: 'manual', payment_reference: '', notes: '' })
                          setDuesModal(m)
                        }}
                          className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md bg-[#E1F5EE] text-[#0F6E56] font-medium hover:bg-[#006B3C] hover:text-white transition-colors">
                          <CheckCircle size={11} /> Reinstate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* ── LEADERSHIP ── */}
            <TabsContent value="leadership" className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-justify text-[12px] text-muted-foreground">
                    Executive committee positions and current holders.
                    Assigning a new president vacates all current positions.
                  </p>
                </div>
                <button
                  onClick={() => setTransferModal(true)}
                  className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 bg-[#C8A84B] text-[#0D2240] rounded-lg font-semibold hover:bg-[#b8943d] transition-colors whitespace-nowrap flex-shrink-0"
                >
                  <Crown size={12} /> Assign President
                </button>
              </div>

              {/* positions table */}
              <div className="rounded-xl border overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr style={{ backgroundColor: '#0D2240' }}>
                      <th className="text-left px-4 py-2.5 font-semibold text-[#F0E4B8]">#</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-[#F0E4B8]">Position</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-[#F0E4B8]">Holder</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-[#F0E4B8]">Since</th>
                      <th className="text-right px-4 py-2.5 font-semibold text-[#F0E4B8]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(positions || []).map((pos: any, idx: number) => {
                      const filled = filledMap[pos.id]
                      const holderName = filled?.association_members?.journalist_profiles?.full_name
                      return (
                        <tr key={pos.id} className={idx % 2 === 1 ? 'bg-[#F7F5F0]' : 'bg-background'}>
                          <td className="px-4 py-2.5 text-muted-foreground">{pos.rank}</td>
                          <td className="px-4 py-2.5 font-medium">
                            {pos.title}
                            {pos.title === 'President' && (
                              <Crown size={10} className="inline ml-1 text-[#C8A84B]" />
                            )}
                          </td>
                          <td className="px-4 py-2.5">
                            {filled ? (
                              <span className="text-foreground">{holderName ?? 'Unknown'}</span>
                            ) : (
                              <span className="text-muted-foreground italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {filled ? new Date(filled.tenure_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            {filled ? (
                              pos.title !== 'President' && (
                                <button
                                  onClick={() => setVacateConfirm(filled.id)}
                                  className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-muted text-muted-foreground hover:bg-[#FEE8E8] hover:text-[#A32D2D] transition-colors ml-auto"
                                >
                                  <UserMinus size={11} /> Vacate
                                </button>
                              )
                            ) : (
                              <button
                                onClick={() => { setAssignModal({ positionId: pos.id, title: pos.title }); setAssignMemberId('') }}
                                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-[#EBF4FF] text-[#1E4E8C] font-medium hover:bg-[#1E4E8C] hover:text-white transition-colors ml-auto"
                              >
                                <Plus size={11} /> Assign
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                    {(positions || []).length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-muted-foreground text-[12px]">
                          <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                          Loading positions…
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* tenure history note */}
              <p className="text-justify text-[11px] text-muted-foreground">
                Full tenure history is recorded in the database for audit purposes.
              </p>
            </TabsContent>

            {/* ── CHAPTERS ── */}
            <TabsContent value="chapters" className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-justify text-[12px] text-muted-foreground">
                  {(chapters || []).length} country chapters configured
                </p>
                <button onClick={() => setChapterModal(true)}
                  className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 bg-[#006B3C] text-white rounded-lg font-medium hover:bg-[#005A32] transition-colors">
                  <Plus size={12} /> Add Chapter
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(chapters || []).map((ch: any) => (
                  <div key={ch.id} className="bg-background border border-border rounded-lg p-3">
                    <div className="text-[13px] font-medium">{ch.country_name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{ch.country_code}</div>
                  </div>
                ))}
              </div>
              {(chapters || []).length === 0 && (
                <div className="text-center py-8 text-[13px] text-muted-foreground">No chapters yet.</div>
              )}
              {selected?.region?.toLowerCase().includes('west africa') && (chapters || []).length === 0 && (
                <button
                  onClick={async () => {
                    for (const c of ECOWAS_COUNTRIES) {
                      await (supabase.from('association_chapters' as any).insert({ association_id: selected.id, country_code: c.code, country_name: c.name }).select() as any)
                    }
                    qc.invalidateQueries({ queryKey: ['association-chapters', selected.id] })
                    toast({ title: 'All 16 ECOWAS chapters added.' })
                  }}
                  className="w-full text-[12px] py-2.5 border border-dashed border-[#006B3C]/40 rounded-lg text-[#006B3C] hover:bg-[#E1F5EE] transition-colors font-medium"
                >
                  + Add all 16 ECOWAS member state chapters
                </button>
              )}
            </TabsContent>

            {/* ── LINKED INSTITUTIONS ── */}
            <TabsContent value="orgs" className="space-y-3">
              <p className="text-justify text-[12px] text-muted-foreground">Institutions that use this association for journalist verification.</p>
              {(linkedOrgs || []).length === 0 && (
                <div className="text-center py-8 text-[13px] text-muted-foreground">No institutions linked yet.</div>
              )}
              {(linkedOrgs || []).map((link: any) => (
                <div key={link.id} className="flex items-center justify-between bg-background border border-border rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className="text-muted-foreground" />
                    <span className="text-[13px] font-medium">{link.organizations?.name}</span>
                  </div>
                  {link.require_membership && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0D2240] text-white font-medium">Membership Required</span>
                  )}
                </div>
              ))}
            </TabsContent>

            {/* ── SETTINGS ── */}
            <TabsContent value="settings" className="space-y-5">
              <div className="bg-muted/30 rounded-xl p-4 space-y-3">
                <Row label="Dues Amount" value={`${selected?.dues_currency} ${parseFloat(selected?.dues_amount || 0).toFixed(2)} / ${selected?.dues_period}`} />
                <Row label="Grace Period" value={`${selected?.grace_period_days} days after due date`} />
                <Row label="Region" value={selected?.region || ''} />
                <Row label="Headquarters" value={selected?.headquarter_country || ''} />
                <Row label="Contact" value={selected?.contact_email || ''} />
                <Row label="Website" value={selected?.website_url || ''} />
              </div>

              {/* ── currencies ── */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-[13px] font-semibold text-[#0D2240] flex items-center gap-1.5">
                      <Coins size={14} /> Accepted Currencies
                    </h4>
                    <p className="text-justify text-[11px] text-muted-foreground mt-0.5">
                      Members see dues converted to these currencies. Set exchange rates relative to the primary currency.
                    </p>
                  </div>
                  <button
                    onClick={() => setCurrencyModal(true)}
                    className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 bg-[#0D2240] text-white rounded-lg font-medium hover:bg-[#0D2240]/80 transition-colors">
                    <Plus size={12} /> Add Currency
                  </button>
                </div>

                {(currencies || []).length === 0 ? (
                  <div className="text-center py-6 text-[12px] text-muted-foreground">No currencies configured yet.</div>
                ) : (
                  <div className="rounded-xl border overflow-hidden">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr style={{ backgroundColor: '#F7F5F0' }}>
                          <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Currency</th>
                          <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Rate (vs primary)</th>
                          <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Dues Equivalent</th>
                          <th className="text-right px-4 py-2 font-semibold text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(currencies || []).map((c: any) => {
                          const equivalent = parseFloat(selected?.dues_amount || 0) * parseFloat(c.exchange_rate_to_primary || 1)
                          return (
                            <tr key={c.id} className="border-t border-border">
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold">{c.currency_code}</span>
                                  {c.is_primary && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#C8A84B]/20 text-[#7A5C00] font-semibold">PRIMARY</span>
                                  )}
                                  {!c.is_active && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">INACTIVE</span>
                                  )}
                                </div>
                                {c.currency_name && <div className="text-[10px] text-muted-foreground">{c.currency_name}</div>}
                              </td>
                              <td className="px-4 py-2.5 text-muted-foreground">
                                {c.is_primary ? '1 (base)' : `×${parseFloat(c.exchange_rate_to_primary).toFixed(4)}`}
                              </td>
                              <td className="px-4 py-2.5 font-medium">
                                {c.currency_code} {equivalent.toLocaleString('en', { maximumFractionDigits: 2 })} / {selected?.dues_period}
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center justify-end gap-2">
                                  {!c.is_primary && (
                                    <>
                                      <button
                                        onClick={() => toggleCurrencyMutation.mutate({ id: c.id, active: !c.is_active })}
                                        className="text-[11px] px-2 py-0.5 rounded border border-border hover:bg-muted transition-colors"
                                      >
                                        {c.is_active ? 'Disable' : 'Enable'}
                                      </button>
                                      <button
                                        onClick={() => deleteCurrencyMutation.mutate(c.id)}
                                        className="text-[11px] px-2 py-0.5 rounded border border-[#FEE8E8] text-[#A32D2D] hover:bg-[#FEE8E8] transition-colors"
                                      >
                                        Remove
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <p className="text-justify text-[11.5px] text-muted-foreground">
                To edit core association settings (name, region, dues amount), contact CONORBIT support or update directly in Supabase.
              </p>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* ─── Create Association Sheet ─── */}
      <Sheet open={showCreate} onOpenChange={setShowCreate}>
        <SheetContent side="right" className="w-full max-w-md overflow-y-auto">
          <SheetHeader className="mb-4"><SheetTitle>Create Association</SheetTitle></SheetHeader>
          <div className="space-y-4">
            <Field label="Association Name *" value={createForm.name} onChange={v => setCreateForm(f => ({ ...f, name: v }))} placeholder="e.g. Association of West Africa Legislative Correspondents" />
            <Field label="Slug *" value={createForm.slug} onChange={v => setCreateForm(f => ({ ...f, slug: v }))} placeholder="e.g. awalco" />
            <Field label="Description" value={createForm.description} onChange={v => setCreateForm(f => ({ ...f, description: v }))} placeholder="Brief description" textarea />
            <Field label="Region" value={createForm.region} onChange={v => setCreateForm(f => ({ ...f, region: v }))} placeholder="e.g. West Africa" />
            <Field label="Headquarter Country" value={createForm.headquarter_country} onChange={v => setCreateForm(f => ({ ...f, headquarter_country: v }))} placeholder="e.g. Nigeria" />
            <Field label="Contact Email" value={createForm.contact_email} onChange={v => setCreateForm(f => ({ ...f, contact_email: v }))} placeholder="secretary@awalco.org" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Dues Amount" value={createForm.dues_amount} onChange={v => setCreateForm(f => ({ ...f, dues_amount: v }))} placeholder="100" />
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Currency</label>
                <select value={createForm.dues_currency} onChange={e => setCreateForm(f => ({ ...f, dues_currency: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                  {COMMON_CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}, {c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Dues Period</label>
                <select value={createForm.dues_period} onChange={e => setCreateForm(f => ({ ...f, dues_period: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                  <option value="annual">Annual</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <Field label="Grace Period (days)" value={createForm.grace_period_days} onChange={v => setCreateForm(f => ({ ...f, grace_period_days: v }))} placeholder="30" />
            </div>
            <button
              disabled={!createForm.name || !createForm.slug || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="w-full py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-50 transition-colors">
              {createMutation.isPending ? 'Creating…' : 'Create Association'}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── Mark Dues Paid modal ─── */}
      {duesModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-[15px] font-semibold mb-1">Record Payment, {duesModal.journalist_profiles?.full_name}</h3>
            <p className="text-justify text-[12px] text-muted-foreground mb-4">Mark dues as paid and set the coverage period.</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Period Start *" value={duesForm.period_start} onChange={v => setDuesForm(f => ({ ...f, period_start: v }))} type="date" />
                <Field label="Period End *" value={duesForm.period_end} onChange={v => setDuesForm(f => ({ ...f, period_end: v }))} type="date" />
              </div>
              <Field label="Amount Paid *" value={duesForm.amount} onChange={v => setDuesForm(f => ({ ...f, amount: v }))} placeholder={selected?.dues_amount} />
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Payment Method</label>
                <select value={duesForm.payment_method} onChange={e => setDuesForm(f => ({ ...f, payment_method: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                  <option value="manual">Manual / Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="flutterwave">Flutterwave</option>
                  <option value="paystack">Paystack</option>
                  <option value="mpesa">M-Pesa</option>
                </select>
              </div>
              <Field label="Reference / Receipt No." value={duesForm.payment_reference} onChange={v => setDuesForm(f => ({ ...f, payment_reference: v }))} placeholder="Optional" />
              <Field label="Notes" value={duesForm.notes} onChange={v => setDuesForm(f => ({ ...f, notes: v }))} placeholder="Optional notes" />
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDuesModal(null)} className="flex-1 py-2.5 border border-border rounded-lg text-[13px] font-medium hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!duesForm.period_start || !duesForm.period_end || !duesForm.amount || markDuesMutation.isPending}
                onClick={() => markDuesMutation.mutate()}
                className="flex-1 py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-50 transition-colors">
                {markDuesMutation.isPending ? 'Saving…' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add Chapter modal ─── */}
      {chapterModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-[15px] font-semibold mb-4">Add Country Chapter</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Country</label>
                <select value={chapterForm.country_code}
                  onChange={e => { const c = ECOWAS_COUNTRIES.find(x => x.code === e.target.value); setChapterForm({ country_code: e.target.value, country_name: c?.name ?? '' }) }}
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                  <option value="">Select country</option>
                  {ECOWAS_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setChapterModal(false)} className="flex-1 py-2.5 border border-border rounded-lg text-[13px] font-medium hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!chapterForm.country_code || addChapterMutation.isPending}
                onClick={() => addChapterMutation.mutate()}
                className="flex-1 py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-50 transition-colors">
                {addChapterMutation.isPending ? 'Adding…' : 'Add Chapter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Assign Position modal ─── */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-[15px] font-semibold mb-1">Assign {assignModal.title}</h3>
            <p className="text-justify text-[12px] text-muted-foreground mb-4">Select an active member to hold this position.</p>
            <div>
              <label className="text-[12px] font-medium text-muted-foreground block mb-1">Member</label>
              <select value={assignMemberId} onChange={e => setAssignMemberId(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                <option value="">Select member…</option>
                {activeMembers.map((m: any) => (
                  <option key={m.id} value={m.id}>{m.journalist_profiles?.full_name ?? m.id} {m.journalist_profiles?.email ? `(${m.journalist_profiles.email})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setAssignModal(null); setAssignMemberId('') }}
                className="flex-1 py-2.5 border border-border rounded-lg text-[13px] font-medium hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!assignMemberId || assignPositionMutation.isPending}
                onClick={() => assignPositionMutation.mutate({ positionId: assignModal.positionId, memberId: assignMemberId })}
                className="flex-1 py-2.5 bg-[#0D2240] text-white rounded-lg text-[13px] font-medium hover:bg-[#0D2240]/80 disabled:opacity-50 transition-colors">
                {assignPositionMutation.isPending ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Transfer Presidency modal ─── */}
      {transferModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <Crown size={16} className="text-[#C8A84B]" />
              <h3 className="text-[15px] font-semibold">Assign / Transfer Presidency</h3>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-[11.5px] text-amber-800">
              <strong>Important:</strong> Assigning a new president will <strong>vacate all current exco positions</strong>.
              The new president will need to assemble their own team.
            </div>
            <div>
              <label className="text-[12px] font-medium text-muted-foreground block mb-1">New President</label>
              <select value={transferMemberId} onChange={e => setTransferMemberId(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                <option value="">Select member…</option>
                {activeMembers.map((m: any) => (
                  <option key={m.id} value={m.id}>{m.journalist_profiles?.full_name ?? m.id} {m.journalist_profiles?.email ? `(${m.journalist_profiles.email})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setTransferModal(false); setTransferMemberId('') }}
                className="flex-1 py-2.5 border border-border rounded-lg text-[13px] font-medium hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!transferMemberId || transferPresidencyMutation.isPending}
                onClick={() => transferPresidencyMutation.mutate(transferMemberId)}
                className="flex-1 py-2.5 bg-[#C8A84B] text-[#0D2240] rounded-lg text-[13px] font-semibold hover:bg-[#b8943d] disabled:opacity-50 transition-colors">
                {transferPresidencyMutation.isPending ? 'Transferring…' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Vacate confirm modal ─── */}
      {vacateConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-xs p-6">
            <h3 className="text-[14px] font-semibold mb-2">Vacate position?</h3>
            <p className="text-justify text-[12px] text-muted-foreground mb-5">This will immediately remove the current holder from this position. The position will become vacant.</p>
            <div className="flex gap-3">
              <button onClick={() => setVacateConfirm(null)} className="flex-1 py-2 border border-border rounded-lg text-[13px] hover:bg-muted transition-colors">Cancel</button>
              <button
                onClick={() => vacatePositionMutation.mutate(vacateConfirm)}
                disabled={vacatePositionMutation.isPending}
                className="flex-1 py-2 bg-[#A32D2D] text-white rounded-lg text-[13px] font-medium hover:bg-red-700 disabled:opacity-50 transition-colors">
                {vacatePositionMutation.isPending ? 'Vacating…' : 'Vacate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Association Detail Sheet ─── */}
      <AssociationDetailSheet
        associationId={detailAssocId}
        onClose={() => setDetailAssocId(null)}
      />

      {/* ─── Add Currency modal ─── */}
      {currencyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-[15px] font-semibold mb-4">Add Currency</h3>
            <div className="space-y-3">
              <div>
                <label className="text-[12px] font-medium text-muted-foreground block mb-1">Currency</label>
                <select value={currencyForm.currency_code}
                  onChange={e => {
                    const c = COMMON_CURRENCIES.find(x => x.code === e.target.value)
                    setCurrencyForm(f => ({ ...f, currency_code: e.target.value, currency_name: c?.name ?? f.currency_name }))
                  }}
                  className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background">
                  <option value="">Select currency…</option>
                  {COMMON_CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}, {c.name}</option>)}
                </select>
              </div>
              <Field label="Currency Name" value={currencyForm.currency_name} onChange={v => setCurrencyForm(f => ({ ...f, currency_name: v }))} placeholder="e.g. Nigerian Naira" />
              <div>
                <Field
                  label={`Exchange Rate (1 ${(currencies || []).find((c: any) => c.is_primary)?.currency_code ?? selected?.dues_currency ?? 'USD'} = ? ${currencyForm.currency_code || '?'})`}
                  value={currencyForm.exchange_rate_to_primary}
                  onChange={v => setCurrencyForm(f => ({ ...f, exchange_rate_to_primary: v }))}
                  placeholder="e.g. 1550 for NGN"
                />
                {currencyForm.exchange_rate_to_primary && currencyForm.currency_code && parseFloat(currencyForm.exchange_rate_to_primary) > 0 && (
                  <p className="text-justify text-[11px] text-muted-foreground mt-1">
                    Dues preview: {currencyForm.currency_code} {(parseFloat(selected?.dues_amount || 0) * parseFloat(currencyForm.exchange_rate_to_primary)).toLocaleString('en', { maximumFractionDigits: 2 })} / {selected?.dues_period}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setCurrencyModal(false); setCurrencyForm({ currency_code: '', currency_name: '', exchange_rate_to_primary: '1' }) }}
                className="flex-1 py-2.5 border border-border rounded-lg text-[13px] font-medium hover:bg-muted transition-colors">Cancel</button>
              <button
                disabled={!currencyForm.currency_code || addCurrencyMutation.isPending}
                onClick={() => addCurrencyMutation.mutate()}
                className="flex-1 py-2.5 bg-[#0D2240] text-white rounded-lg text-[13px] font-medium hover:bg-[#0D2240]/80 disabled:opacity-50 transition-colors">
                {addCurrencyMutation.isPending ? 'Saving…' : 'Add Currency'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[12.5px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

function Field({ label, value, onChange, placeholder, textarea, type }: {
  label: string; value: string; onChange: (v: string) => void
  placeholder?: string; textarea?: boolean; type?: string
}) {
  return (
    <div>
      <label className="text-[12px] font-medium text-muted-foreground block mb-1">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3}
          className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background focus:outline-none focus:border-[#006B3C] resize-none" />
      ) : (
        <input type={type ?? 'text'} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="w-full border border-border rounded-lg px-3 py-2 text-[13px] bg-background focus:outline-none focus:border-[#006B3C]" />
      )}
    </div>
  )
}
